import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface TocEntry {
  id: string;
  label: string;
  children?: TocEntry[];
}

const ACTIVE_OFFSET_PX = 140;

/**
 * Sticky in-page navigation for the long-form pages. Highlights the last
 * section whose top has scrolled past the sticky header.
 */
export const SideNav: React.FC<{ entries: TocEntry[]; heading?: string }> = ({
  entries,
  heading,
}) => {
  const { t } = useTranslation("sideNav");
  const resolvedHeading = heading ?? t("defaultHeading");
  const [active, setActive] = useState(entries[0]?.id ?? "");

  useEffect(() => {
    const ids = entries.flatMap((e) => [e.id, ...(e.children?.map((c) => c.id) ?? [])]);

    const update = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (atBottom) {
        setActive(ids[ids.length - 1]);
        return;
      }
      let current = ids[0];
      for (const id of ids) {
        const node = document.getElementById(id);
        if (node && node.getBoundingClientRect().top <= ACTIVE_OFFSET_PX) current = id;
      }
      setActive(current);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [entries]);

  const linkClass = (isActive: boolean, sub: boolean) =>
    `-ml-px block border-l-2 py-1.5 leading-snug transition ${sub ? "pl-8 text-[0.8125rem]" : "pl-4"} ${
      isActive
        ? "border-accent font-semibold text-ink"
        : "border-transparent text-ink-400 hover:border-sand-300 hover:text-ink-600"
    }`;

  return (
    <nav aria-label={resolvedHeading} className="text-sm">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink-400">
        {resolvedHeading}
      </p>
      <ul className="space-y-0.5 border-l border-sand-200">
        {entries.map((entry) => (
          <li key={entry.id}>
            <a href={`#${entry.id}`} className={linkClass(entry.id === active, false)}>
              {entry.label}
            </a>
            {entry.children && (
              <ul>
                {entry.children.map((child) => (
                  <li key={child.id}>
                    <a href={`#${child.id}`} className={linkClass(child.id === active, true)}>
                      {child.label}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

/**
 * Long-form page scaffold: intro band, then content with a sticky sidebar.
 */
export const LongPage: React.FC<{
  intro: React.ReactNode;
  toc: TocEntry[];
  tocHeading?: string;
  children: React.ReactNode;
}> = ({ intro, toc, tocHeading, children }) => (
  <>
    <div className="border-b border-sand-200 bg-white">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">{intro}</div>
    </div>
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="gap-14 py-14 lg:flex">
        <aside className="mb-10 shrink-0 lg:mb-0 lg:w-56">
          <div className="lg:sticky lg:top-28">
            <SideNav entries={toc} heading={tocHeading} />
          </div>
        </aside>
        <div className="min-w-0 max-w-3xl flex-1">{children}</div>
      </div>
    </div>
  </>
);
